/* =========================================================
   Flexi › Exposure Register — pure logic shared by the overview, the wizard
   and the template editor. No Angular, no HTTP: everything here is unit
   tested in exposure-register.logic.spec.ts.
   ========================================================= */

import {
  ExposureAssessmentDepartment,
  ExposureAssessmentFactor,
  ExposureAssessmentSection,
  ExposureAssessmentStep,
  ExposureAssessmentSummary,
  ExposureHealthCheck,
  ExposureTemplateFactor,
} from './exposure-register.models';

/**
 * The status marker of a factor. Each one has its own SHAPE, so the outcome
 * never relies on colour alone: a filled circle, a triangle, a hollow circle
 * and a dashed circle.
 */
export type HealthCheckMarker = 'relevant' | 'unclear' | 'not-relevant' | 'not-assessed';

/** The three outcomes, in the order the segmented control shows them. */
export const HEALTH_CHECK_OPTIONS: readonly ExposureHealthCheck[] = [
  ExposureHealthCheck.Relevant,
  ExposureHealthCheck.Unclear,
  ExposureHealthCheck.NotRelevant,
];

/** The summary step's status filter. */
export type ExposureStatusFilter = 'all' | ExposureHealthCheck;

export const ALL_STEPS: readonly ExposureAssessmentStep[] = [
  ExposureAssessmentStep.Introduction,
  ExposureAssessmentStep.Departments,
  ExposureAssessmentStep.Assessment,
  ExposureAssessmentStep.Summary,
];

export function healthCheckMarker(healthCheck: ExposureHealthCheck | null | undefined): HealthCheckMarker {
  switch (healthCheck) {
    case ExposureHealthCheck.Relevant:
      return 'relevant';
    case ExposureHealthCheck.Unclear:
      return 'unclear';
    case ExposureHealthCheck.NotRelevant:
      return 'not-relevant';
    default:
      return 'not-assessed';
  }
}

/** Translation key of the outcome's word ("Relevant", "Not assessed", ...). */
export function healthCheckLabelKey(healthCheck: ExposureHealthCheck | null | undefined): string {
  switch (healthCheck) {
    case ExposureHealthCheck.Relevant:
      return 'FLEXI_EXPOSURE.HEALTH_CHECK.RELEVANT';
    case ExposureHealthCheck.Unclear:
      return 'FLEXI_EXPOSURE.HEALTH_CHECK.UNCLEAR';
    case ExposureHealthCheck.NotRelevant:
      return 'FLEXI_EXPOSURE.HEALTH_CHECK.NOT_RELEVANT';
    default:
      return 'FLEXI_EXPOSURE.HEALTH_CHECK.NOT_ASSESSED';
  }
}

export function isAssessed(factor: Pick<ExposureAssessmentFactor, 'HealthCheck'>): boolean {
  return factor.HealthCheck !== null && factor.HealthCheck !== undefined;
}

/** A wizard step from a `?step=` query value, or null when it is not 1–4. */
export function parseStep(value: string | null | undefined): ExposureAssessmentStep | null {
  const step = Number(value);
  return ALL_STEPS.includes(step as ExposureAssessmentStep) ? (step as ExposureAssessmentStep) : null;
}

/** Sections and their factors in display order. Returns copies; the input is untouched. */
export function sortedSections(sections: readonly ExposureAssessmentSection[]): ExposureAssessmentSection[] {
  return [...sections]
    .sort((a, b) => a.Position - b.Position)
    .map(section => ({
      ...section,
      Factors: [...section.Factors].sort((a, b) => a.Position - b.Position || a.Id - b.Id),
    }));
}

export function allFactors(sections: readonly ExposureAssessmentSection[]): ExposureAssessmentFactor[] {
  return sections.flatMap(section => section.Factors);
}

/** Assessed / total and the count per outcome. */
export interface ExposureProgress {
  assessed: number;
  total: number;
  /** 0–100, rounded down so "almost done" never reads as 100 %. */
  percent: number;
  relevant: number;
  unclear: number;
  notRelevant: number;
}

export function exposureProgress(factors: readonly Pick<ExposureAssessmentFactor, 'HealthCheck'>[]): ExposureProgress {
  const total = factors.length;
  const assessed = factors.filter(isAssessed).length;
  return {
    assessed,
    total,
    percent: total === 0 ? 0 : Math.floor((assessed * 100) / total),
    relevant: factors.filter(f => f.HealthCheck === ExposureHealthCheck.Relevant).length,
    unclear: factors.filter(f => f.HealthCheck === ExposureHealthCheck.Unclear).length,
    notRelevant: factors.filter(f => f.HealthCheck === ExposureHealthCheck.NotRelevant).length,
  };
}

/** Percentage for a progress bar from server-side counts. */
export function percentOf(part: number, total: number): number {
  return total <= 0 ? 0 : Math.min(100, Math.floor((part * 100) / total));
}

/** Departments in display order. */
export function sortedDepartments(departments: readonly ExposureAssessmentDepartment[]): ExposureAssessmentDepartment[] {
  return [...departments].sort((a, b) => a.Position - b.Position || a.Name.localeCompare(b.Name));
}

export function includedDepartments(departments: readonly ExposureAssessmentDepartment[]): ExposureAssessmentDepartment[] {
  return sortedDepartments(departments).filter(department => department.IsIncluded);
}

/**
 * The names of the given departments, in department order, comma separated.
 * Ids that are not (or no longer) included departments are left out.
 */
export function departmentNames(
  departmentIds: readonly number[],
  departments: readonly ExposureAssessmentDepartment[],
): string {
  return includedDepartments(departments)
    .filter(department => departmentIds.includes(department.DepartmentId))
    .map(department => department.Name)
    .join(', ');
}

/**
 * A factor's department ids without the departments that are no longer
 * included — the backend drops them when a department is excluded in step 2.
 */
export function pruneDepartmentIds(
  departmentIds: readonly number[],
  departments: readonly ExposureAssessmentDepartment[],
): number[] {
  const included = new Set(includedDepartments(departments).map(department => department.DepartmentId));
  return departmentIds.filter(id => included.has(id));
}

/** Adds the department when absent, removes it when present. */
export function toggleId(ids: readonly number[], id: number): number[] {
  return ids.includes(id) ? ids.filter(existing => existing !== id) : [...ids, id];
}

/** Included departments that at least one Relevant factor applies to. */
export function departmentsWithRelevantExposure(
  sections: readonly ExposureAssessmentSection[],
  departments: readonly ExposureAssessmentDepartment[],
): number {
  const included = new Set(includedDepartments(departments).map(department => department.DepartmentId));
  const exposed = new Set<number>();
  for (const factor of allFactors(sections)) {
    if (factor.HealthCheck === ExposureHealthCheck.Relevant) {
      factor.DepartmentIds.filter(id => included.has(id)).forEach(id => exposed.add(id));
    }
  }
  return exposed.size;
}

/** One section of the summary table with the factors that pass the filters. */
export interface ExposureSummaryGroup {
  name: string;
  factors: ExposureAssessmentFactor[];
}

/**
 * The summary step's table: sections in order, each with the factors that
 * match the department (null = all) and status filter. Sections left empty
 * by the filter are dropped.
 */
export function summaryGroups(
  sections: readonly ExposureAssessmentSection[],
  departmentId: number | null,
  status: ExposureStatusFilter,
): ExposureSummaryGroup[] {
  return sortedSections(sections)
    .map(section => ({
      name: section.Name,
      factors: section.Factors.filter(factor =>
        (status === 'all' || factor.HealthCheck === status)
        && (departmentId === null || factor.DepartmentIds.includes(departmentId))),
    }))
    .filter(group => group.factors.length > 0);
}

/**
 * What still stands between the assessment and completion. Mirrors the
 * backend's 400: at least one included department and every factor assessed.
 */
export interface ExposureCompletionCheck {
  includedDepartments: number;
  unassessed: number;
  canComplete: boolean;
}

export function completionCheck(
  departments: readonly ExposureAssessmentDepartment[],
  sections: readonly ExposureAssessmentSection[],
): ExposureCompletionCheck {
  const factors = allFactors(sections);
  const included = includedDepartments(departments).length;
  const unassessed = factors.filter(factor => !isAssessed(factor)).length;
  return {
    includedDepartments: included,
    unassessed,
    canComplete: factors.length > 0 && included > 0 && unassessed === 0,
  };
}

/** Guidance text as paragraphs: split on blank lines, trimmed, empty ones dropped. */
export function guidanceParagraphs(text: string | null | undefined): string[] {
  return (text ?? '')
    .split(/\r?\n\s*\r?\n/)
    .map(paragraph => paragraph.trim())
    .filter(paragraph => paragraph.length > 0);
}

/** The overview's search: title or responsible, case-insensitive. */
export function filterAssessments(
  rows: readonly ExposureAssessmentSummary[],
  search: string,
): ExposureAssessmentSummary[] {
  const needle = search.trim().toLocaleLowerCase();
  if (!needle) {
    return [...rows];
  }
  return rows.filter(row =>
    `${row.Title} ${row.ResponsibleUserName ?? ''}`.toLocaleLowerCase().includes(needle));
}

/** Fallback download name when the server does not expose one. */
export function reportFileName(title: string): string {
  const safe = title.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
  return `${safe || 'report'}.pdf`;
}

// ---------------------------------------------------------------------------
// Template editor
// ---------------------------------------------------------------------------

/** A section of a template while it is being edited. */
export interface FactorSectionDraft {
  name: string;
  factors: ExposureTemplateFactor[];
}

/** Template factors grouped into editable sections, in their stored order. */
export function toFactorSections(factors: readonly ExposureTemplateFactor[]): FactorSectionDraft[] {
  const sorted = [...factors].sort((a, b) => a.SectionPosition - b.SectionPosition || a.Position - b.Position);
  const sections: FactorSectionDraft[] = [];
  for (const factor of sorted) {
    const last = sections[sections.length - 1];
    const copy = { ...factor, RegulatoryReferences: factor.RegulatoryReferences.map(ref => ({ ...ref })) };
    if (last && last.name === factor.SectionName) {
      last.factors.push(copy);
    } else {
      sections.push({ name: factor.SectionName, factors: [copy] });
    }
  }
  return sections;
}

/**
 * Flattens edited sections back into the save request's factor list,
 * renumbering SectionPosition and Position from the on-screen order (both
 * 1-based). Empty sections are dropped: a section exists only through its
 * factors.
 */
export function flattenFactorSections(sections: readonly FactorSectionDraft[]): ExposureTemplateFactor[] {
  const result: ExposureTemplateFactor[] = [];
  let sectionPosition = 0;
  for (const section of sections) {
    if (!section.factors.length) {
      continue;
    }
    sectionPosition++;
    section.factors.forEach((factor, index) => {
      result.push({
        SectionName: section.name.trim(),
        SectionPosition: sectionPosition,
        Position: index + 1,
        Title: factor.Title.trim(),
        Guidance: factor.Guidance?.trim() ? factor.Guidance.trim() : null,
        RegulatoryReferences: factor.RegulatoryReferences
          .map(ref => ({ Title: ref.Title.trim(), Url: ref.Url?.trim() ? ref.Url.trim() : null }))
          .filter(ref => ref.Title.length > 0),
      });
    });
  }
  return result;
}
